import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { ChipsTrigger } from '@hh.ru/magritte-ui-chips-trigger';
import { Input } from '@hh.ru/magritte-ui-input';
import { Select } from '@hh.ru/magritte-ui-select';
import { Suggest } from '@hh.ru/magritte-ui-suggest';

function areValuesEqual(chips, selectValues) {
    const filteredChipsSet = new Set();
    chips.forEach((item) => {
        if (!item.userInput) {
            filteredChipsSet.add(item.value);
        }
    });
    if (filteredChipsSet.size !== selectValues.length) {
        return false;
    }
    return selectValues.every(({ value }) => filteredChipsSet.has(value));
}
const getUniqueChips = (selectState, chipsFromState) => {
    const chipsFromUserInput = chipsFromState.filter(({ userInput }) => !!userInput);
    // добавить значения селекта (преобразовать в чипсы их сперва)
    const chipsFromSelect = selectState.length > 0 ? selectState.map(({ value, text }) => ({ value, label: text, userInput: false })) : [];
    return [...chipsFromSelect, ...chipsFromUserInput];
};
const getInitialSelectValue = (type, chips) => {
    if (type === 'suggest') {
        return [];
    }
    return chips.map((chip) => ({ value: chip.value, text: chip.label }));
};
const ChipsInputComponent = ({ type, state, onChange: onChangeExternal, triggerProps, suggestProps, selectProps }, ref) => {
    const chipsTriggerRef = useRef(null);
    const chipsTriggerRefCallback = useMultipleRefs(ref, chipsTriggerRef);
    const { allowArbitraryValues } = triggerProps;
    const [selectState, setSelectState] = useState(() => getInitialSelectValue(type, state.chips));
    const selectStateRef = useRef(selectState);
    const optionsCountRef = useRef(0);
    const onChangeSuggestRef = useRef();
    const { isMobile } = useBreakpoint();
    useEffect(() => {
        if (type === 'suggest') {
            return;
        }
        if (areValuesEqual(state.chips, selectStateRef.current)) {
            return;
        }
        const newSelectState = getInitialSelectValue(type, state.chips);
        selectStateRef.current = newSelectState;
        setSelectState(newSelectState);
    }, [type, state.chips]);
    if (type === 'suggest') {
        return (jsx(Suggest, { ...suggestProps, onChange: (value, isValueFromList) => {
                if (!isValueFromList) {
                    return;
                }
                if (!state.chips.some((chip) => chip.value === value)) {
                    onChangeExternal({ value: '', chips: [...state.chips, { value }] });
                    if (isMobile) {
                        onChangeSuggestRef.current?.('');
                    }
                }
            }, onBottomSheetOpen: () => chipsTriggerRef.current?.blur(), input: {
                component: Input,
                props: {
                    value: state.value,
                    'data-qa': 'chips-input-suggest-search',
                },
            }, onSearchSubmit: (event, onCloseBottomSheet, optionsCount) => {
                if (!allowArbitraryValues || optionsCount > 0) {
                    return;
                }
                const value = event.target.value;
                if (value) {
                    onChangeExternal({ value: '', chips: [...state.chips, { value }] });
                    onCloseBottomSheet();
                    onChangeSuggestRef.current?.('');
                }
            }, renderTrigger: ({ props, optionsCount }) => {
                const { onChange, wrapperRef, onFocus, onKeyDown } = props;
                onChangeSuggestRef.current = onChange;
                return (jsx(ChipsTrigger, { ...triggerProps, 
                    // разрешаем добавлять результаты только если в suggest ничего не нашли
                    allowArbitraryValues: optionsCount === 0 && allowArbitraryValues, state: state, ref: chipsTriggerRefCallback, wrapperRef: wrapperRef, onKeyDown: onKeyDown, onFocus: onFocus, "data-qa": triggerProps['data-qa'] || 'chips-input-suggest-trigger', onChange: (newState) => {
                        onChangeExternal(newState);
                        onChange?.(newState.value);
                    } }));
            } }));
    }
    const { applyChangesButton, clearButton } = selectProps;
    const haveApplyChangesButton = Boolean(applyChangesButton);
    const haveClearButton = Boolean(clearButton);
    return (jsx(Select, { ...selectProps, searchable: true, multiple: true, value: selectState, onSelectOption: (values) => {
            selectStateRef.current = values;
        }, onChange: (newValues) => {
            setSelectState(newValues);
            if (haveApplyChangesButton || (haveClearButton && newValues.length === 0)) {
                // обработаем эти кейсы в onApply/onClear
                return;
            }
            const uniqueChips = getUniqueChips(newValues, state.chips);
            onChangeExternal({
                value: '',
                chips: uniqueChips,
            });
        }, onOptionsChange: (options) => {
            optionsCountRef.current = options.length;
        }, onApply: ({ isMobile, searchValue }) => {
            const uniqueChips = getUniqueChips(selectStateRef.current, state.chips);
            // добавляем пользовательский ввод, если нужно
            // в случае bs - из navbar, в случае drop - из chis-trigger
            const userValue = isMobile ? searchValue : state.value;
            if (allowArbitraryValues && userValue.length > 0 && optionsCountRef.current === 0) {
                uniqueChips.push({ value: userValue });
            }
            onChangeExternal({
                value: '',
                chips: uniqueChips,
            });
            // актуализируем состояние селекта
            setSelectState(selectStateRef.current);
        }, onClear: () => {
            onChangeExternal({
                value: '',
                chips: [],
            });
            setSelectState([]);
            selectStateRef.current = [];
        }, onSearchSubmit: (event, _, optionsCount, onCloseBottomSheetWithUpdateValues) => {
            const uniqueChips = getUniqueChips(selectStateRef.current, state.chips);
            // добавляем пользовательский ввод из bs, если нужно
            const userValue = event.target.value;
            if (allowArbitraryValues && userValue.length > 0 && optionsCount === 0) {
                uniqueChips.push({ value: userValue, userInput: true });
            }
            onChangeExternal({
                value: '',
                chips: uniqueChips,
            });
            setSelectState(selectStateRef.current);
            onCloseBottomSheetWithUpdateValues();
        }, triggerProps: { size: 'large' }, renderTrigger: ({ ref, setSearchValue, onKeyDown, onBlur, onFocus, optionsCount }) => {
            optionsCountRef.current = optionsCount;
            return (jsx(ChipsTrigger, { ...triggerProps, allowArbitraryValues: optionsCount === 0 && allowArbitraryValues, state: state, ref: chipsTriggerRefCallback, wrapperRef: ref, onKeyDown: onKeyDown, onBlur: onBlur, onFocus: onFocus, "data-qa": triggerProps['data-qa'] || 'chips-input-select-trigger', onChange: (newState) => {
                    const { event } = newState;
                    setSearchValue(newState.value);
                    // если нажали `enter` в инпуте на desktop
                    if (event?.type === 'add' && event.modifier === 'enter') {
                        const uniqueChips = getUniqueChips(selectStateRef.current, newState.chips);
                        onChangeExternal({
                            value: '',
                            chips: uniqueChips,
                        });
                        setSelectState(selectStateRef.current);
                        return;
                    }
                    onChangeExternal(newState);
                    // если обновился только input, то ререндрить select не нужно
                    if (event?.type === 'change' && event.modifier === 'keyboard') {
                        return;
                    }
                    // осталось обработать только remove
                    const deletedChip = event?.element?.value;
                    const newSelectState = selectStateRef.current.filter((item) => item.value !== deletedChip);
                    setSelectState(newSelectState);
                    selectStateRef.current = newSelectState;
                } }));
        } }));
};
const ChipsInput = forwardRef(ChipsInputComponent);
ChipsInput.displayName = 'ChipsInput';

export { ChipsInput };
//# sourceMappingURL=ChipsInput.js.map
